import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  GridItem,
  VStack,
  Text,
  Image,
  Portal,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { productService, formatProductsForMegaMenu } from "../../services/productService";
import { getProductLinks } from "../../constants/navigationData";
import { useScrollToClose } from "./hooks/useScrollToClose";
import { useNavigate } from "react-router-dom";

// Static product mapping for specific routes
const staticProductRoutes = {
  'ossp': '/products/ossp',
  'insightdb': '/products/insightdb',
  'ebs': '/products/ebs',
  'queuepro': '/products/queuepro',
  'smart-delivery-platform': '/products/smart-delivery-platform',
  // 'digital-corporate-banking': '/products/digital-corporate-banking',
  'insight-db': '/products/insightdb',
  'queue-pro': '/products/queuepro',
  'one-stop-service-platform': '/products/ossp',
  'enterprise-business-solutions': '/products/ebs',
  'business-intelligence-database': '/products/insightdb',
  'queue-management-system': '/products/queuepro',
  'ecommerce-platform': '/products/smart-delivery-platform'
};

// Function to get product route based on title or ID
const getProductRoute = (product) => {
  // Check if it's a static product by title (case insensitive)
  const titleKey = product.title?.toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  if (staticProductRoutes[titleKey]) {
    return staticProductRoutes[titleKey];
  }
  
  // Check by common name variations
  const title = product.title?.toLowerCase() || '';
  if (title.includes('ossp') || title.includes('one stop')) {
    return '/products/ossp';
  }
  if (title.includes('queue')) {
    return '/products/queuepro';
  }
  if (title.includes('ebs') || title.includes('enterprise')) {
    return '/products/ebs';
  }
  if (title.includes('insight') || title.includes('business intelligence')) {
    return '/products/insightdb';
  }
  if (title.includes('smart delivery') || title.includes('ecommerce')) {
    return '/products/smart-delivery-platform';
  }
  // if (title.includes('digital') && title.includes('banking')) {
  //   return '/products/digital-corporate-banking';
  // }
  
  // Default to generic product details route for API products
  return `/product-details/${product.id}`;
};

const ProductsMegaMenu = ({ isOpen, onClose, onMouseEnter, onMouseLeave }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  
  // API products state
  const [apiProducts, setApiProducts] = useState([]);
  const [fallbackProducts, setFallbackProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch both API products and fallback products
  useEffect(() => {
    const fetchProductsData = async () => {
      try {
        setLoading(true);
        
        // Fetch API products
        const result = await productService.getAllProducts();
        
        if (result.success && result.data) {
          const megaMenuProducts = formatProductsForMegaMenu(result.data);
          setApiProducts(megaMenuProducts);
          setError(null);
        } else {
          setError(result.error || 'Failed to fetch products data');
        }
        
        // Always fetch fallback products from navigationData
        const fallback = await getProductLinks();
        const fallbackFormatted = fallback.map((product) => ({
          id: product.id,
          title: product.title,
          description: product.description,
          image: product.image,
          href: product.href,
          isNavigationProduct: true
        }));
        setFallbackProducts(fallbackFormatted);
      } catch (err) {
        console.error('Error fetching products data:', err);
        setError('Failed to load products data');
      } finally {
        setLoading(false);
      }
    };

    fetchProductsData();
  }, []);

  // Use only our 5 main static products, ignore API products for now
  const allProducts = fallbackProducts;

  // Use scroll-to-close hook
  useScrollToClose(isOpen, onClose);
  const navigate = useNavigate();

  return (
    <Portal>
      <Box
        position="fixed"
        top="60px"
        left="0"
        right="0"
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        zIndex="dropdown"
        display={isOpen ? "block" : "none"}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-mega-menu
      >
        <Container maxW="8xl" py={8}>
          {/* Show loading spinner while fetching API data */}
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
              <Spinner size="lg" color="#000B8C" />
            </Box>
          )}
          
          {/* Show error message if API fails but still show fallback products */}
          {error && !loading && apiProducts.length === 0 && (
            <Alert status="warning" mb={4}>
              <AlertIcon />
              Showing fallback product list. Some features may be limited.
            </Alert>
          )}
          
          {/* Products Grid - Always show products (API or fallback) */}
          {!loading && allProducts.length > 0 && (
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
                xl: "repeat(5, 1fr)",
              }}
              gap={4}
            >
              {allProducts.map((product, index) => (
              <GridItem
                key={index}
                onClick={() => {
                  // Close the mega menu first
                  onClose();
                  
                  // Get the appropriate route for this product
                  const productRoute = getProductRoute(product);
                  
                  // Navigate to the determined route
                  navigate(productRoute, {
                    state: {
                      title: product.title,
                      description: product.description,
                      productData: product,
                      isApiProduct: !product.isNavigationProduct,
                      isNavigationProduct: product.isNavigationProduct
                    },
                  });
                }}
                cursor="pointer"
              >
                <Box
                  borderRadius="xl"
                  overflow="hidden"
                  // bg="white"
                  _hover={{
                    transform: "translateY(-4px)",
                    cursor: "pointer",
                  }}
                  transition="all 0.3s"
                  height="180px" // Fixed height
                  width="100%" // Full width of grid cell
                  display="flex"
                  flexDirection="column"
                >
                  <Box position="relative" height="80px" flexShrink={0}>
                    <Image
                      src={product.image}
                      align={"left"}
                      p={4}
                      alt={product.title}
                      w="full"
                      h="full"
                      objectFit="contain"
                    />
                  </Box>
                  <VStack
                    align="start"
                    spacing={2}
                    p={3}
                    flex={1}
                    justify="space-between"
                  >
                    <Box>
                      <Text
                        fontWeight="bold"
                        fontSize={{ base: "sm", md: "md" }}
                        color={textColor}
                        lineHeight="short"
                        mb={2}
                      >
                        {product.title}
                      </Text>
                      <Text
                        fontSize={{ base: "xs", md: "sm" }}
                        color="gray.600"
                        lineHeight="base"
                        noOfLines={3}
                      >
                        {product.description}
                      </Text>
                    </Box>
                    
                  </VStack>
                </Box>
              </GridItem>
            ))}
          </Grid>
          )}
        </Container>
      </Box>
    </Portal>
  );
};

export default ProductsMegaMenu;
